import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Form, Input, Select, Switch, Row, Col, Tooltip, TreeSelect, Typography, Spin} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {ModelDTO, VEAPActivity} from 'hemwb-api';
import {
  fields as metadataFieldLabels,
  hardCodedOptions,
  requiredFields as metadataRequiredFields,
  FieldKey,
} from '../ModelEdit/ModelEditMetadata/constants';
import {
  useVeevaMdpLoaderWithParams,
  useVeevaMdpModelLanguageLoaderWithParams,
  useVeevaVeapLoaderWithParams,
  useVeevaDocMetadataLoaderWithParams,
} from '../../../../../store/veeva/hooks';
import {useModelMetadataActiveOptions, useModelMetadataAssociations} from '../../../../../store/modelMetadata';
import {ModelTreeNode} from '../../../../../store/modelFile';
import {isEmptyValue, resolveCountryGlobal, VeevaDocMetadata} from '../ModelEdit/ModelEditMetadata/veevaAutofillUtils';

const {Text} = Typography;
const {TextArea} = Input;

/**
 * Highlights matching text in a string by wrapping matches in a span with teal background
 */
const highlightText = (text: string, searchTerm: string): React.ReactNode => {
  if (!text || !searchTerm.trim()) {
    return text;
  }

  const lowerText = text.toLowerCase();
  const lowerSearch = searchTerm.toLowerCase();
  const startIdx = lowerText.indexOf(lowerSearch);

  if (startIdx === -1) {
    return text;
  }

  return (
    <>
      {text.substring(0, startIdx)}
      <span style={{backgroundColor: '#00a0a0', fontWeight: 500}}>
        {text.substring(startIdx, startIdx + searchTerm.length)}
      </span>
      {text.substring(startIdx + searchTerm.length)}
    </>
  );
};

export interface StepMetadataCloneRedesignedProps {
  model: ModelDTO;
  clonedFrom: ModelDTO;
  files: ModelTreeNode[];
  onChange: (updatedModel: ModelDTO) => void;
  onStatusChange: (isValid: boolean) => void;
}

const VERSION_PATTERN = /^\d+(\.\d+)*$/;

const normaliseProduct = (p?: string | string[]) =>
  (Array.isArray(p) ? p : (p || '').split('|'))
    .map((s: string) => s.trim().toLowerCase())
    .filter(Boolean)
    .sort()
    .join('|');

const StepMetadataCloneRedesigned: React.FC<StepMetadataCloneRedesignedProps> = ({
  model,
  clonedFrom,
  files,
  onChange,
  onStatusChange,
}) => {
  const [form] = Form.useForm();
  const formInitialized = useRef(false);

  // Same hook used by StepMetadataRedesigned and ModelEditMetadataForm
  const options = useModelMetadataActiveOptions();

  const reportValidity = useCallback(() => {
    form
      .validateFields({validateOnly: true})
      .then(() => onStatusChange(true))
      .catch(() => onStatusChange(false));
  }, [form, onStatusChange]);

  // Re-validate once options finish loading so validity is reported correctly
  // Also trigger version field validation here — this is when Form.Item fields first render
  // (if options weren't cached, the form shows <Spin> until now, so fields weren't in the DOM earlier)
  useEffect(() => {
    if (options && formInitialized.current) {
      setTimeout(() => {
        form.validateFields(['version_release_of_model']).catch(() => {});
        reportValidity();
      }, 0);
    }
  }, [options]);

  // Study number cascade
  const associations = useModelMetadataAssociations();
  const prevStudyNumber = useRef<string | null>(null);

  // Veeva / MDP
  const loadVeap = useVeevaVeapLoaderWithParams();
  const loadMdp = useVeevaMdpLoaderWithParams();
  const loadMdpModelLanguage = useVeevaMdpModelLanguageLoaderWithParams();
  const loadDocMetadata = useVeevaDocMetadataLoaderWithParams();
  const [revopsOptions, setRevopsOptions] = useState<VEAPActivity[]>([]);
  const [revopsLoading, setRevopsLoading] = useState(false);
  const [revopsSearchTerm, setRevopsSearchTerm] = useState('');
  const [mdp, setMdp] = useState<string[]>([]);
  const [languageAutoPopulated, setLanguageAutoPopulated] = useState(false);
  const userEditedCountryOrGlobalRef = useRef(false);
  const userEditedLanguageRef = useRef(false);

  // Default to global=true if country is not available from VVMC
  const [isGlobalModel, setIsGlobalModel] = useState(model.metadata?.global === 'true' || !model.metadata?.country);
  const [enableWebModel, setEnableWebModel] = useState(model.metadata?.platform === 'WebModel 2.0');

  const loadRevopsList = useCallback(
    (searchTerm?: string, isIdLookup = false) => {
      if (!searchTerm) {
        setRevopsOptions([]);
        setRevopsSearchTerm('');
        return Promise.resolve([] as VEAPActivity[]);
      }

      setRevopsSearchTerm(searchTerm);
      setRevopsLoading(true);
      return loadVeap([searchTerm])
        .then((response) => {
          const veapItems = Array.isArray(response) ? (response as unknown as VEAPActivity[]) : [];
          // Filter to only show items that:
          // 1. Start with MDA (the VEAP ID prefix)
          // 2. When searching by name (user-typed search), also require the name to contain the search term.
          //    When loading by ID (isIdLookup=true), skip the name-contains check because the search term
          //    is a VEAP activity ID (e.g. "VEAP-OLD"), not a name prefix.
          const mdaItems = veapItems.filter(
            (item) =>
              item.name?.startsWith('MDA') &&
              (isIdLookup || item.name?.toUpperCase().includes(searchTerm.toUpperCase())),
          );
          setRevopsOptions(mdaItems);
          return mdaItems;
        })
        .finally(() => setRevopsLoading(false));
    },
    [loadVeap],
  );

  const loadMdpList = useCallback(
    (veapId?: string) => {
      if (veapId) {
        loadMdp([veapId]).then((response) => {
          if (Array.isArray(response)) {
            setMdp(response.map((r: any) => r.url));
          } else {
            setMdp([]);
          }
        });
      } else {
        setMdp([]);
      }
    },
    [loadMdp],
  );

  // Initialize form once
  useEffect(() => {
    if (formInitialized.current) return;
    formInitialized.current = true;

    // Resolve the REV/OPS ID: new flow stores it in revops_id, old flow stored it in veap_id
    const resolvedRevopsId = model.metadata?.revops_id || model.metadata?.veap_id || '';

    form.setFieldsValue({
      revops_id: undefined, // Leave empty so spinner shows while loading
      study_number: model.metadata?.study_number ?? '',
      type: model.metadata?.type ?? '',
      information_classification: model.metadata?.information_classification ?? '',
      product: model.metadata?.product ? model.metadata.product.split('|') : [],
      disease: model.metadata?.disease ?? '',
      therapeutic_area: model.metadata?.therapeutic_area ?? '',
      model_type: model.metadata?.model_type ?? '',
      country: model.metadata?.country ?? '',
      stage_of_model_development: model.metadata?.stage_of_model_development ?? '',
      objective_of_model: model.metadata?.objective_of_model ?? '',
      notes: model.metadata?.notes ?? '',
      links: model.metadata?.links ?? '',
      version_release_of_model: model.metadata?.version_release_of_model ?? '',
      version_log: model.metadata?.version_log ?? '',
      changelog_file: model.metadata?.changelog_file ?? '',
      model_development_plan: model.metadata?.model_development_plan ?? '',
    });

    if (resolvedRevopsId) {
      // Load the revops list by ID to get the name and populate the form
      loadRevopsList(resolvedRevopsId, true).then((loadedOptions) => {
        // Once options are loaded, set the form field to the first option's ID so Select displays the name
        if (Array.isArray(loadedOptions) && loadedOptions.length > 0) {
          form.setFieldValue('revops_id', loadedOptions[0].id);
          loadMdpList(resolvedRevopsId);
        }
      });
    }

    // Report initial validity; version error displayed via options useEffect once fields are rendered
    setTimeout(reportValidity, 0);
  }, []);

  const buildUpdatedModel = useCallback(
    (allValues: any, globalOverride?: boolean) => {
      const effectiveGlobal = globalOverride !== undefined ? globalOverride : isGlobalModel;
      return {
        ...model,
        metadata: {
          ...model.metadata,
          ...Object.keys(allValues).reduce((acc: any, key: string) => {
            if (key === 'product' && Array.isArray(allValues[key])) {
              acc['product'] = allValues[key].join('|');
            } else {
              acc[key] = allValues[key];
            }
            return acc;
          }, {}),
          // Mirror revops_id → veap_id so the backend always receives both fields
          veap_id: allValues['revops_id'] ?? model.metadata?.veap_id ?? '',
          global: effectiveGlobal ? 'true' : 'false',
          platform: enableWebModel ? 'WebModel 2.0' : 'Model Repository',
        },
      };
    },
    [model, isGlobalModel, enableWebModel],
  );

  const handleValuesChange = useCallback(
    (changedValues: any, allValues: any) => {
      if ('type' in changedValues) {
        setLanguageAutoPopulated(false);
      }

      // Country set → clear global
      let globalOverride: boolean | undefined;
      if (changedValues.country && isGlobalModel) {
        setIsGlobalModel(false);
        globalOverride = false;
      }

      // Study number cascade
      if ('study_number' in changedValues && associations) {
        const sn = changedValues.study_number;
        if (sn && sn !== prevStudyNumber.current && associations.studyNumbers?.[sn]) {
          prevStudyNumber.current = sn;
          const assoc = associations.studyNumbers[sn];
          const cascaded: any = {};
          if (assoc.disease) cascaded.disease = assoc.disease;
          if (assoc.therapeutic_area) cascaded.therapeutic_area = assoc.therapeutic_area;
          if (assoc.product) cascaded.product = assoc.product.split('|');
          if (Object.keys(cascaded).length) {
            form.setFieldsValue(cascaded);
            Object.assign(allValues, cascaded);
          }
        }
      }

      // REVOPS change → reload MDP
      if ('revops_id' in changedValues) {
        loadMdpList(changedValues.revops_id);
        if (!changedValues.revops_id) {
          form.setFieldsValue({model_development_plan: undefined});
          allValues.revops_name = undefined;
        } else {
          const selectedActivity = revopsOptions.find((opt) => opt.id === changedValues.revops_id);
          if (selectedActivity) {
            allValues.revops_name = selectedActivity.name;
          }
        }
      }

      // Re-validate version when lineage fields change
      if (
        'disease' in changedValues ||
        'therapeutic_area' in changedValues ||
        'product' in changedValues ||
        'country' in changedValues
      ) {
        form.validateFields(['version_release_of_model']).catch(() => {});
      }

      const updatedModel = buildUpdatedModel(allValues, globalOverride);
      onChange(updatedModel);

      // Defer so Ant Design finishes its own internal validation cycle first
      setTimeout(reportValidity, 0);
    },
    [
      associations,
      buildUpdatedModel,
      form,
      isGlobalModel,
      loadMdpList,
      onChange,
      onStatusChange,
      reportValidity,
      revopsOptions,
    ],
  );

  const versionRules = useMemo(
    () => [
      {required: true, message: 'Version number is required'},
      {
        pattern: VERSION_PATTERN,
        message: 'Version must match format like "1" or "1.1"',
      },
      ({getFieldValue}: any) => ({
        validator(_: any, value: string) {
          if (!value) return Promise.resolve();
          const disease = getFieldValue('disease') ?? '';
          const ta = getFieldValue('therapeutic_area') ?? '';
          const product = getFieldValue('product') || [];
          const country = getFieldValue('country') ?? '';
          const sameLineage =
            disease === (clonedFrom.metadata?.disease ?? '') &&
            ta === (clonedFrom.metadata?.therapeutic_area ?? '') &&
            normaliseProduct(product) === normaliseProduct(clonedFrom.metadata?.product) &&
            isGlobalModel === (clonedFrom.metadata?.global === 'true') &&
            country === (clonedFrom.metadata?.country ?? '');
          if (sameLineage && parseFloat(value) <= parseFloat(clonedFrom.metadata?.version_release_of_model || '0')) {
            return Promise.reject(
              new Error(`Version must be greater than ${clonedFrom.metadata?.version_release_of_model}`),
            );
          }
          return Promise.resolve();
        },
      }),
    ],
    [clonedFrom, isGlobalModel],
  );

  const handleRevopsSelect = useCallback(
    (selectedRevopsId?: string) => {
      if (!selectedRevopsId) {
        // Only clear dependent fields if there was actually a REV/OPS ID set before
        const previousRevopsId = model.metadata?.revops_id || model.metadata?.veap_id;
        if (!previousRevopsId) {
          return;
        }
        setMdp([]);
        setLanguageAutoPopulated(false);
        userEditedLanguageRef.current = false;
        const clearedValues = {
          revops_id: undefined,
          model_development_plan: undefined,
          revops_name: undefined,
          product: [],
          therapeutic_area: undefined,
          disease: undefined,
        };
        const mergedClearedValues = {
          ...form.getFieldsValue(),
          ...clearedValues,
        };
        form.setFieldsValue(clearedValues);
        handleValuesChange(clearedValues, mergedClearedValues);
        return;
      }

      // Find the selected VEAP item from revopsOptions
      const selectedVeapItem = revopsOptions.find((item) => item.id === selectedRevopsId);

      // Only allow selection of items with MDA prefix
      if (!selectedVeapItem || !selectedVeapItem.name?.startsWith('MDA')) {
        form.setFieldValue('revops_id', undefined);
        return;
      }

      // Pre-populate fields from VEAP response
      const autoFilledValues: any = {
        revops_id: selectedRevopsId,
        revops_name: selectedVeapItem?.name,
        model_development_plan: undefined,
      };

      if (selectedVeapItem) {
        if (selectedVeapItem.primaryIndication) {
          autoFilledValues.disease = selectedVeapItem.primaryIndication;
        }
        if (selectedVeapItem.productName) {
          autoFilledValues.product = selectedVeapItem.productName.split('|').map((p: string) => p.trim());
        }
        if (selectedVeapItem.therapeuticArea) {
          autoFilledValues.therapeutic_area = selectedVeapItem.therapeuticArea;
        }
      }

      const mergedValues = {
        ...form.getFieldsValue(),
        ...autoFilledValues,
      };

      form.setFieldsValue(autoFilledValues);
      handleValuesChange(autoFilledValues, mergedValues);

      // Load MDP URLs from Veeva API
      loadMdpList(selectedRevopsId);

      // Load document-level metadata and auto-fill only empty fields (non-blocking, AC-01, AC-02, AC-05, AC-07)
      loadDocMetadata([selectedRevopsId])
        .then((rawDocMetadata: unknown) => {
          const docMetadata = rawDocMetadata as VeevaDocMetadata | null;
          if (!docMetadata) return;
          const latestFormValues = form.getFieldsValue();
          const docAutoFilledValues: any = {};
          let globalOverride: boolean | undefined;

          // Country / Global toggle semantics (AC-01, AC-02): only if user has not manually edited
          if (!userEditedCountryOrGlobalRef.current) {
            const {country: resolvedCountry, isGlobal} = resolveCountryGlobal(docMetadata.country);
            docAutoFilledValues.country = resolvedCountry;
            globalOverride = isGlobal;
            setIsGlobalModel(isGlobal);
            form.validateFields(['country', 'version_release_of_model']).catch(() => {});
          }

          // Other metadata fields — apply only when empty
          if (isEmptyValue(latestFormValues.disease) && docMetadata.disease) {
            docAutoFilledValues.disease = docMetadata.disease;
          }
          if (isEmptyValue(latestFormValues.therapeutic_area) && docMetadata.therapeuticArea) {
            docAutoFilledValues.therapeutic_area = docMetadata.therapeuticArea;
          }
          if (isEmptyValue(latestFormValues.model_type) && docMetadata.modelType) {
            docAutoFilledValues.model_type = docMetadata.modelType;
          }
          if (isEmptyValue(latestFormValues.information_classification) && docMetadata.classification) {
            docAutoFilledValues.information_classification = docMetadata.classification;
          }
          if (isEmptyValue(latestFormValues.version_release_of_model) && docMetadata.version) {
            docAutoFilledValues.version_release_of_model = docMetadata.version;
          }

          // Propagate owner hints to metadata for downstream permission step (AC-05, AC-07, FR-05)
          if (docMetadata.owner && isEmptyValue(latestFormValues.owner)) {
            docAutoFilledValues.owner = docMetadata.owner;
          }
          if (
            Array.isArray(docMetadata.owners) &&
            docMetadata.owners.length > 0 &&
            isEmptyValue(latestFormValues.owners)
          ) {
            docAutoFilledValues.owners = docMetadata.owners;
          }

          if (Object.keys(docAutoFilledValues).length === 0) return;

          form.setFieldsValue(docAutoFilledValues);
          const merged = {...latestFormValues, ...docAutoFilledValues};
          const updatedModel = buildUpdatedModel(merged, globalOverride);
          onChange(updatedModel);
          setTimeout(reportValidity, 0);
        })
        .catch(() => {
          // Non-blocking: doc metadata fetch failed — continue without interrupting the user
        });
    },
    [
      form,
      model.metadata,
      revopsOptions,
      handleValuesChange,
      loadMdpList,
      loadDocMetadata,
      buildUpdatedModel,
      onChange,
      reportValidity,
    ],
  );

  const handleMdpSelect = useCallback(
    (selectedMdpUrl?: string) => {
      if (!selectedMdpUrl) {
        setLanguageAutoPopulated(false);
        return;
      }

      // AC-06: do not overwrite if user has manually edited Model Language
      if (userEditedLanguageRef.current) {
        setLanguageAutoPopulated(false);
        return;
      }

      loadMdpModelLanguage([selectedMdpUrl, hardCodedOptions.type])
        .then((response) => {
          const detectedLanguage = response?.modelLanguage;
          if (detectedLanguage && hardCodedOptions.type.includes(detectedLanguage)) {
            form.setFieldValue('type', detectedLanguage);
            const mergedValues = {
              ...form.getFieldsValue(),
              type: detectedLanguage,
            };
            handleValuesChange({type: detectedLanguage}, mergedValues);
            setLanguageAutoPopulated(true);
            return;
          }
          setLanguageAutoPopulated(false);
        })
        .catch(() => {
          setLanguageAutoPopulated(false);
        });
    },
    [form, handleValuesChange, loadMdpModelLanguage],
  );

  const requiredMark = (label: React.ReactNode, required: boolean) =>
    required ? (
      <span>
        {label} <span style={{color: '#ff4d4f'}}>*</span>
      </span>
    ) : (
      <span>{label}</span>
    );

  if (!options) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', padding: '48px'}}>
        <Spin tip="Loading options..." />
      </div>
    );
  }

  // ─── LAYOUT_REPLACE_MARKER ───

  return (
    <Form form={form} layout="vertical" onValuesChange={handleValuesChange}>
      {/* Model Identification */}
      <div style={{marginBottom: 24}}>
        <h4
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: '#00a0a0',
            margin: 0,
            padding: 0,
            fontFamily: 'inherit',
            lineHeight: 1.4,
          }}
        >
          Model Identification
        </h4>

        {/* Row 1: REV/OPS ID | Name of Product */}
        <Row gutter={32}>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  {metadataFieldLabels['revops_id']} {/* removed required asterisk for revops_id */}{' '}
                  <Tooltip title="Select to auto-populate related metadata fields">
                    <InfoCircleOutlined />
                  </Tooltip>{' '}
                  <span style={{fontWeight: 400, color: '#8c8c8c', fontSize: '12px'}}>
                    (Select to auto-populate related metadata fields)
                  </span>
                </span>
              }
              name="revops_id"
            >
              <Select
                placeholder="Find REV/OPS ID"
                allowClear
                showSearch
                loading={revopsLoading}
                optionLabelProp="label"
                onSearch={loadRevopsList}
                onChange={handleRevopsSelect}
                filterOption={false}
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
                notFoundContent="Type to search"
                options={revopsOptions.map((item) => ({
                  label: item.name,
                  value: item.id,
                  item,
                }))}
                optionRender={(opt) => {
                  const item = (opt.data as any).item as VEAPActivity;
                  return (
                    <div style={{fontSize: 12}}>
                      <div>
                        <Text strong>VEAP ID:</Text> {highlightText(item.name || '', revopsSearchTerm)}
                      </div>
                      <div>
                        <Text strong>Title:</Text> {highlightText(item.projectTitle || '', revopsSearchTerm)}
                      </div>
                      <div>
                        <Text strong>Product:</Text> {highlightText(item.productName || '', revopsSearchTerm)}
                      </div>
                    </div>
                  );
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={requiredMark(metadataFieldLabels['product'], !!metadataRequiredFields['product' as FieldKey])}
              name="product"
              required={false}
              rules={[{required: !!metadataRequiredFields['product' as FieldKey], message: 'Product is required'}]}
              colon={false}
            >
              <TreeSelect
                placeholder="Select Product"
                allowClear
                treeCheckable
                showCheckedStrategy={TreeSelect.SHOW_PARENT}
                showSearch
                treeData={options.products.map((p) => ({title: p, value: p, key: p}))}
                filterTreeNode={(input, node) =>
                  (node?.title?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 2: Trial Number | Name of Disease */}
        <Row gutter={32}>
          <Col span={12}>
            <Form.Item label={metadataFieldLabels['study_number']} name="study_number">
              <Select
                placeholder="Find Trial Number"
                allowClear
                showSearch
                options={options.studyNumbers.map((s) => ({label: s, value: s}))}
                filterOption={(input, opt) =>
                  (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={requiredMark(metadataFieldLabels['disease'], !!metadataRequiredFields['disease' as FieldKey])}
              name="disease"
              required={false}
              rules={[{required: !!metadataRequiredFields['disease' as FieldKey], message: 'Disease is required'}]}
              colon={false}
            >
              <Select
                placeholder="Select Name of Disease"
                allowClear
                showSearch
                options={options.diseases.map((d) => ({label: d, value: d}))}
                filterOption={(input, opt) =>
                  (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 3: Model Language | Therapeutic Area */}
        <Row gutter={32}>
          <Col span={12}>
            <Form.Item
              label={requiredMark(metadataFieldLabels['type'], !!metadataRequiredFields['type' as FieldKey])}
              name="type"
              extra={
                languageAutoPopulated
                  ? 'Auto-populated from Model Development Plan. You can still override this value.'
                  : undefined
              }
              required={false}
              rules={[{required: !!metadataRequiredFields['type' as FieldKey], message: 'Model Language is required'}]}
              colon={false}
            >
              <Select
                placeholder="Select Model Language"
                allowClear
                showSearch
                onChange={() => {
                  userEditedLanguageRef.current = true;
                  setLanguageAutoPopulated(false);
                }}
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
                options={hardCodedOptions.type?.map((t) => ({
                  label: t === 'ExcelNative' ? 'Excel' : t,
                  value: t,
                }))}
                filterOption={(input, opt) =>
                  (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={requiredMark(
                metadataFieldLabels['therapeutic_area'],
                !!metadataRequiredFields['therapeutic_area' as FieldKey],
              )}
              name="therapeutic_area"
              required={false}
              rules={[
                {
                  required: !!metadataRequiredFields['therapeutic_area' as FieldKey],
                  message: 'Therapeutic Area is required',
                },
              ]}
              colon={false}
            >
              <Select
                placeholder="Select Therapeutic Area"
                allowClear
                showSearch
                options={options.therapeuticAreas.map((a) => ({label: a, value: a}))}
                filterOption={(input, opt) =>
                  (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 4: Information Classification | Model Type */}
        <Row gutter={32}>
          <Col span={12}>
            <Form.Item
              label={requiredMark(
                metadataFieldLabels['information_classification'],
                !!metadataRequiredFields['information_classification' as FieldKey],
              )}
              name="information_classification"
              required={false}
              rules={[
                {
                  required: !!metadataRequiredFields['information_classification' as FieldKey],
                  message: 'Information Classification is required',
                },
              ]}
              colon={false}
            >
              <Select
                placeholder="Select Information Classification"
                allowClear
                showSearch
                options={hardCodedOptions.information_classification?.map((c) => ({label: c, value: c}))}
                filterOption={(input, opt) =>
                  (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={requiredMark(
                metadataFieldLabels['model_type'],
                !!metadataRequiredFields['model_type' as FieldKey],
              )}
              name="model_type"
              required={false}
              rules={[
                {required: !!metadataRequiredFields['model_type' as FieldKey], message: 'Model Type is required'},
              ]}
              colon={false}
            >
              <Select
                placeholder="Select Model Type"
                allowClear
                showSearch
                options={options.modelTypes.map((m) => ({label: m, value: m}))}
                filterOption={(input, opt) =>
                  (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Row 5: (empty) | Enable WebModel 2.0 */}
        <Row gutter={32}>
          <Col span={12} />
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Enable WebModel 2.0{' '}
                  <Tooltip title="WebModel 2.0 is a set of features in Model Repository that enables model deployment, execution, and visualization for scripted language models (e.g., Mathematica).">
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              }
            >
              <Switch
                checked={enableWebModel}
                onChange={(checked) => {
                  setEnableWebModel(checked);
                  const allValues = form.getFieldsValue();
                  const updatedModel = {
                    ...model,
                    metadata: {
                      ...model.metadata,
                      ...Object.keys(allValues).reduce((acc: any, key: string) => {
                        if (key === 'product' && Array.isArray(allValues[key])) {
                          acc['product'] = allValues[key].join('|');
                        } else {
                          acc[key] = allValues[key];
                        }
                        return acc;
                      }, {}),
                      global: isGlobalModel ? 'true' : 'false',
                      platform: checked ? 'WebModel 2.0' : 'Model Repository',
                    },
                  };
                  onChange(updatedModel);
                }}
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* Country Association */}
      <div style={{marginBottom: 24}}>
        <h4
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: '#00a0a0',
            margin: 0,
            padding: 0,
            marginBottom: 16,
            fontFamily: 'inherit',
            lineHeight: 1.4,
          }}
        >
          Country Association
        </h4>
        <Row gutter={32}>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  Is this a Global Model?{' '}
                  <Tooltip title="Global models are not country-specific">
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              }
            >
              <Switch
                checked={isGlobalModel}
                onChange={(checked) => {
                  userEditedCountryOrGlobalRef.current = true;
                  setIsGlobalModel(checked);
                  if (checked) {
                    form.setFieldsValue({country: undefined});
                  }
                  form.validateFields(['country', 'version_release_of_model']).catch(() => {});
                  const allValues = form.getFieldsValue();
                  const updatedModel = buildUpdatedModel(allValues, checked);
                  onChange(updatedModel);
                  setTimeout(reportValidity, 0);
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={requiredMark(
                metadataFieldLabels['country'],
                !!metadataRequiredFields['country' as FieldKey] && !isGlobalModel,
              )}
              name="country"
              required={false}
              rules={[
                {
                  required: !!metadataRequiredFields['country' as FieldKey] && !isGlobalModel,
                  message: 'Country is required',
                },
              ]}
              colon={false}
            >
              <Select
                placeholder="Select Country"
                allowClear
                showSearch
                disabled={isGlobalModel}
                options={options.countries.map((c) => ({label: c, value: c}))}
                filterOption={(input, opt) =>
                  (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={() => {
                  userEditedCountryOrGlobalRef.current = true;
                }}
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* Documentation */}
      <div style={{marginBottom: 24}}>
        <h4
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: '#00a0a0',
            margin: 0,
            padding: 0,
            marginBottom: 16,
            fontFamily: 'inherit',
            lineHeight: 1.4,
          }}
        >
          Documentation
        </h4>
        <Form.Item label={metadataFieldLabels['objective_of_model']} name="objective_of_model">
          <TextArea rows={3} placeholder="Enter the objective of your model" />
        </Form.Item>
      </div>

      {/* Usage */}
      <div style={{marginBottom: 24}}>
        <h4
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: '#00a0a0',
            margin: 0,
            padding: 0,
            marginBottom: 16,
            fontFamily: 'inherit',
            lineHeight: 1.4,
          }}
        >
          Usage
        </h4>
        <Form.Item label={metadataFieldLabels['notes']} name="notes">
          <TextArea rows={3} placeholder="Notes" />
        </Form.Item>
        <Form.Item
          label={
            <span>
              Training and Publication Links{' '}
              <span style={{fontWeight: 400, color: '#8c8c8c'}}>
                (links to model publications, links to training recordings, etc.)
              </span>
            </span>
          }
          name="links"
          extra="Add links to relevant publications, training materials, or external resources"
        >
          <TextArea rows={3} placeholder="Links" />
        </Form.Item>
      </div>

      {/* Stage */}
      <div style={{marginBottom: 24}}>
        <h4
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: '#00a0a0',
            margin: 0,
            padding: 0,
            marginBottom: 16,
            fontFamily: 'inherit',
            lineHeight: 1.4,
          }}
        >
          Stage
        </h4>
        <Row gutter={32}>
          <Col span={12}>
            <Form.Item label={metadataFieldLabels['model_development_plan']} name="model_development_plan">
              <Select
                placeholder="Select Model Development Plan"
                allowClear
                onChange={handleMdpSelect}
                showSearch
                notFoundContent="No options"
                options={mdp.map((url) => ({label: url, value: url}))}
                filterOption={(input, opt) =>
                  (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={requiredMark(
                metadataFieldLabels['stage_of_model_development'],
                !!metadataRequiredFields['stage_of_model_development' as FieldKey],
              )}
              name="stage_of_model_development"
              required={false}
              rules={[
                {
                  required: !!metadataRequiredFields['stage_of_model_development' as FieldKey],
                  message: 'Stage of Model Development is required',
                },
              ]}
              colon={false}
            >
              <Select
                placeholder="Select Stage of Model Development"
                allowClear
                showSearch
                options={options.stagesOfModelDevelopment.map((s) => ({label: s, value: s}))}
                filterOption={(input, opt) =>
                  (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
                }
                getPopupContainer={() =>
                  (document.querySelector('.MuiDrawer-paper.whiteTheme') as HTMLElement) || document.body
                }
              />
            </Form.Item>
          </Col>
        </Row>
      </div>

      {/* Version Control */}
      <div style={{marginBottom: 24}}>
        <h4
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: '#00a0a0',
            margin: 0,
            padding: 0,
            marginBottom: 16,
            fontFamily: 'inherit',
            lineHeight: 1.4,
          }}
        >
          Version Control
        </h4>
        <Row gutter={32}>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  {metadataFieldLabels['version_release_of_model']} <span style={{color: '#ff4d4f'}}>*</span>
                </span>
              }
              name="version_release_of_model"
              required={false}
              rules={versionRules}
              colon={false}
            >
              <Input placeholder="e.g. 1.1" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label={
                <span>
                  {metadataFieldLabels['changeLogFileName']}{' '}
                  <Tooltip title="Select the changelog document from existing files">
                    <InfoCircleOutlined />
                  </Tooltip>
                </span>
              }
              name="changelog_file"
            >
              <Select
                placeholder="Select Changelog Document"
                allowClear
                options={files.map((f) => ({label: f.name, value: f.uuid}))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label={metadataFieldLabels['version_log']} name="version_log">
          <TextArea rows={3} placeholder="Describe changes from prior versions" />
        </Form.Item>
      </div>
    </Form>
  );
};

export default StepMetadataCloneRedesigned;
